import React, { useCallback, useEffect, useState } from "react";
import { Box, Typography, Divider, Button, Chip, CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import CustomModal from "../../component/common/customModal";
import PaymentSuccess from "../../../assets/images/PaySuccess.svg";
import { InvoiceType } from "../../../types/invoice&payment";
import invoiceService from "../../../services/InvoiceService";
import { showToast } from "../../component/common/toastNotification";
import { generateReceiptPdf } from "../../../helper/generateReceiptPdf";
import {
  formatIqdAmount,
  formatPaymentDisplayDate,
  formatPaymentMethodLabel,
  formatDisplayInvoiceNumber,
  formatSagePaymentRef,
  pickPaymentDate,
  pickSagePaymentNumber,
  resolveInvoiceAmountPaid,
} from "../../../helper/paymentDisplayUtils";
import { localizePaymentStatus } from "../../../helper/localizePaymentStatus";
import { NJIM } from "../../../theme/njimTheme";

function isPaidReceiptStatus(status: string | number | null | undefined): boolean {
  const raw = String(status ?? "").trim().toLowerCase();
  if (!raw) return true;
  return (
    raw === "paid" ||
    raw.includes("paid") ||
    raw.includes("success") ||
    raw.includes("completed") ||
    raw.includes("مدفوع")
  );
}

interface Props {
  open: boolean;
  onClose: () => void;
  invoiceData: InvoiceType | null;
}

const ViewReceiptModal: React.FC<Props> = ({ open, onClose, invoiceData }) => {
  const { t, i18n } = useTranslation();
  const [recieptData, setRecieptData] = useState<InvoiceType>({});
  const [downloading, setDownloading] = useState(false);

  const resolveAmountPaid = (
    payment: { amount?: number },
    invoice: InvoiceType
  ): number => {
    const fromInvoice = resolveInvoiceAmountPaid(invoice);
    if (fromInvoice > 0) return fromInvoice;

    const paymentAmount = Number(payment.amount ?? 0);
    if (paymentAmount > 0) return paymentAmount;

    return 0;
  };

  const handelGetReceipt = useCallback(async () => {
    try {
      if (!invoiceData || !invoiceData.id) {
        showToast(t("invoicesPayments.invoiceDataNotFound"), "error");
        return;
      }

      const response = await invoiceService.getAllPayments();
      const payments: Array<Record<string, unknown>> = Array.isArray(response.data)
        ? response.data
        : [];

      const id = String(invoiceData.id);
      const payment =
        payments.find((p) => String(p.id ?? p.Id) === id) ??
        payments.find((p) => String(p.invoiceId ?? p.InvoiceId) === id) ??
        payments.find((p) => String(p.orderId ?? p.OrderId) === id) ??
        {};

      const sageRaw =
        pickSagePaymentNumber(payment) || invoiceData.sagePaymentNumber || "";
      const paymentDate = pickPaymentDate(payment) ?? invoiceData.paymentDate;
      const paymentType = String(payment.type ?? payment.Type ?? invoiceData.paymentMethod ?? "");
      const pendingSageLabel = t("invoicesPayments.pendingSageSync", {
        defaultValue: "Pending Sage sync",
      });
      const orderNumber = invoiceData.orderNumber ?? "—";
      const isWalletTopUp = orderNumber === "Wallet Top-Up";
      const sageTransactionRef = formatSagePaymentRef(sageRaw, pendingSageLabel);

      setRecieptData({
        invoiceNumber: isWalletTopUp
          ? sageTransactionRef
          : formatDisplayInvoiceNumber(
              invoiceData.invoiceNumber,
              invoiceData.orderNumber
            ) || invoiceData.invoiceNumber,
        orderNumber,
        amountPaid: resolveAmountPaid(
          { amount: Number(payment.amount ?? payment.Amount ?? 0) },
          invoiceData
        ),
        paymentDate: formatPaymentDisplayDate(paymentDate),
        paymentMethod: formatPaymentMethodLabel(paymentType, orderNumber),
        transactionID: sageTransactionRef,
        status: invoiceData.paymentStatus,
      });
    } catch {
      showToast(t("invoicesPayments.invoiceDataNotFound"), "error");
    }
  }, [invoiceData, t]);

  const handleDownloadPdf = async () => {
    if (!recieptData.invoiceNumber && !invoiceData?.invoiceNumber) {
      showToast(t("invoicesPayments.invoiceDataNotFound"), "error");
      return;
    }

    setDownloading(true);
    try {
      const amountFormatted = formatIqdAmount(recieptData.amountPaid ?? 0);
      const invoiceNumber = recieptData.invoiceNumber ?? invoiceData?.invoiceNumber ?? "receipt";
      const isRTL = i18n.language?.toLowerCase().startsWith("ar");

      await generateReceiptPdf(
        {
          title: t("invoicesPayments.paymentReceipt"),
          subtitle: `${t("invoicesPayments.receiptFor")} ${invoiceNumber}`,
          paymentSuccessful: t("invoicesPayments.paymentSuccessful"),
          invoiceNumber,
          orderNumber: recieptData.orderNumber ?? "—",
          amountPaid: amountFormatted,
          paymentDate: recieptData.paymentDate ?? "—",
          paymentMethod: recieptData.paymentMethod ?? "—",
          transactionId: recieptData.transactionID ?? "—",
          status: localizePaymentStatus(recieptData.status ?? "Paid", t),
          thankYou: t("invoicesPayments.thankYouForPayment"),
          companyName: t("invoicesPayments.alHayatPharmaceuticals"),
          labels: {
            invoiceNumber: t("invoicesPayments.invoiceNumberLabel"),
            orderNumber: t("invoicesPayments.orderNumberLabel"),
            amountPaid: t("invoicesPayments.amountPaidLabel"),
            paymentDate: t("invoicesPayments.paymentDateLabel"),
            paymentMethod: t("invoicesPayments.paymentMethodLabel"),
            transactionId: t("invoicesPayments.transactionIdLabel"),
            status: t("invoicesPayments.statusLabel"),
          },
        },
        `Receipt_${invoiceNumber}.pdf`,
        { isRTL }
      );
    } catch {
      showToast(t("invoicesPayments.downloadReceiptFailed"), "error");
    } finally {
      setDownloading(false);
    }
  };

  useEffect(() => {
    if (open) {
      handelGetReceipt();
    }
  }, [open, handelGetReceipt]);

  return (
    <CustomModal
      open={open}
      onClose={onClose}
      ModalTitle={t("invoicesPayments.paymentReceipt")}
      ModalDescription={`${t("invoicesPayments.receiptFor")} ${
        invoiceData?.orderNumber === "Wallet Top-Up"
          ? formatSagePaymentRef(
              invoiceData?.sagePaymentNumber,
              t("invoicesPayments.pendingSageSync", { defaultValue: "Pending Sage sync" })
            )
          : formatDisplayInvoiceNumber(
              invoiceData?.invoiceNumber,
              invoiceData?.orderNumber
            ) || invoiceData?.invoiceNumber
      }`}
      width="450px"
    >
      <Box sx={{ textAlign: "center", pt: 1, pb: 2 }}>
        <Box sx={{
          display: "inline-flex",
          background: "rgba(22,163,74,0.08)",
          borderRadius: "50%",
          p: 1.5,
        }}>
          <img src={PaymentSuccess} alt="Success" style={{ width: 40 }} />
        </Box>

        <Typography sx={{ mt: 1.5, fontWeight: 600, fontSize: "16px", color: NJIM.textPrimary }}>
          {t("invoicesPayments.paymentSuccessful")}
        </Typography>

        <Divider sx={{ my: 2, borderColor: "rgba(226,232,240,0.5)" }} />

        <Box sx={{ width: "100%" }}>
          {recieptData &&
            [
              [
                t("invoicesPayments.invoiceNumberLabel"),
                recieptData.invoiceNumber,
              ],
              [t("invoicesPayments.orderNumberLabel"), recieptData.orderNumber],
              [
                t("invoicesPayments.amountPaidLabel"),
                formatIqdAmount(recieptData.amountPaid ?? 0),
              ],
              [t("invoicesPayments.paymentDateLabel"), recieptData.paymentDate],
              [
                t("invoicesPayments.paymentMethodLabel"),
                recieptData.paymentMethod,
              ],
              [
                t("invoicesPayments.transactionIdLabel"),
                recieptData.transactionID,
              ],
            ].map(([label, value], index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "baseline",
                  gap: 2,
                  py: 1,
                  px: 0.5,
                  borderBottom: "1px solid rgba(226,232,240,0.4)",
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 500,
                    flex: "0 0 auto",
                    whiteSpace: "nowrap",
                    textAlign: "start",
                    textJustify: "none",
                    letterSpacing: "normal",
                    wordSpacing: "normal",
                    fontSize: "13px",
                    color: NJIM.textSecondary,
                  }}
                >
                  {label}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "13px",
                    fontWeight: 500,
                    color: NJIM.textPrimary,
                    textAlign: "end",
                    minWidth: 0,
                  }}
                >
                  {value}
                </Typography>
              </Box>
            ))}

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              py: 1,
              px: 0.5,
              alignItems: "center",
            }}
          >
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: "13px",
                color: NJIM.textSecondary,
                flex: "0 0 auto",
                whiteSpace: "nowrap",
                textJustify: "none",
                letterSpacing: "normal",
                wordSpacing: "normal",
              }}
            >
              {t("invoicesPayments.statusLabel")}
            </Typography>
            <Chip
              label={localizePaymentStatus(recieptData.status ?? "Paid", t)}
              size="small"
              sx={{
                backgroundColor: isPaidReceiptStatus(recieptData.status)
                  ? "rgba(22,163,74,0.1)"
                  : "rgba(220,38,38,0.1)",
                color: isPaidReceiptStatus(recieptData.status) ? NJIM.success : NJIM.danger,
                fontWeight: 600,
                height: 24,
                fontSize: "12px",
                border: isPaidReceiptStatus(recieptData.status)
                  ? "1px solid rgba(22,163,74,0.2)"
                  : "1px solid rgba(220,38,38,0.2)",
              }}
            />
          </Box>
        </Box>

        <Box
          sx={{
            mt: 2,
            p: 2,
            backgroundColor: "rgba(248,249,250,0.5)",
            borderRadius: "10px",
            border: "1px solid rgba(226,232,240,0.4)",
          }}
        >
          <Typography sx={{ color: NJIM.textMuted, fontSize: "13px" }}>
            {t("invoicesPayments.thankYouForPayment")}
          </Typography>
          <Typography sx={{ color: NJIM.textMuted, fontSize: "13px" }}>
            {t("invoicesPayments.alHayatPharmaceuticals")}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            gap: 2,
            mt: 3,
          }}
        >
          <Button
            variant="outlined"
            fullWidth
            disabled={downloading}
            onClick={handleDownloadPdf}
            sx={{
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 600,
              fontSize: "13px",
              borderColor: "rgba(226,232,240,0.8)",
              color: NJIM.ink,
              "&:hover": { borderColor: NJIM.ink, background: "rgba(10,61,120,0.04)" },
            }}
          >
            {downloading ? (
              <CircularProgress size={18} sx={{ color: NJIM.ink }} />
            ) : (
              t("invoicesPayments.downloadPDF")
            )}
          </Button>
        </Box>
      </Box>
    </CustomModal>
  );
};

export default ViewReceiptModal;
